import type { KosztSesjiDto, KosztyOkresuDto, OkresDni, ZrodloKosztow } from "@shared/kosztyTypy.js";
import type {
  CzescTresci,
  ListaWywolanDto,
  LogiDto,
  PodgladSondyDto,
  StanSystemuDto,
  TrescWywolaniaDto,
  WynikSondyDto,
  WywolanieSzczegolDto,
} from "@shared/wywolaniaTypy.js";
import {
  POLE_MOWCA_UZYTKOWNIKA,
  type OdpowiedzMowcyUzytkownikaDto,
  type ZapisMowcyUzytkownikaDto,
} from "@shared/jaTypy.js";
import type { OdpowiedzKonta } from "@shared/kontaTypy.js";
import type { FindingKind, InputKind } from "@shared/ocenaTypy.js";
import type { FiltrPoprawek, LinkRecenzjiAdmin, PoprawkaAdmin, UtworzLinkWejscie } from "@shared/recenzjeTypy.js";
import { nazwaPlikuZNaglowka } from "./naglowekPliku.js";
import { nazwaCsvZNaglowka } from "./recenzje.js";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    /**
     * Reszta pol z odpowiedzi bledu (np. `maxZnakow`, `slug`, `nazwa`, `komunikat`).
     * Serwer zwraca `{ error: kod, ...szczegoly }`, a `code` niesie wylacznie przyczyne.
     */
    readonly szczegoly: Readonly<Record<string, unknown>> = {},
  ) {
    super(`${status} ${code}`);
    this.name = "ApiError";
  }
}

/** Blad z odpowiedzi nieudanej: `{ error: kod, ...szczegoly }`, a bez JSON-a (np. 502 z nginx) sam kod statusu. */
async function bladZOdpowiedzi(res: Response): Promise<ApiError> {
  let code = "unknown";
  let szczegoly: Record<string, unknown> = {};
  try {
    const { error, ...reszta } = (await res.json()) as { error?: unknown } & Record<string, unknown>;
    if (typeof error === "string") code = error;
    szczegoly = reszta;
  } catch {
    // Odpowiedz bez JSON-a, np. 502 z nginx. Zostaje kod statusu.
  }
  return new ApiError(res.status, code, szczegoly);
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) throw await bladZOdpowiedzi(res);
  return (await res.json()) as T;
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  return handle<T>(res);
}

/** Query string bez pustych i niezdefiniowanych wartosci ("" z formularza znaczy "bez filtra"). */
function zapytanie(parametry: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [klucz, wartosc] of Object.entries(parametry)) {
    if (wartosc === undefined || wartosc === "") continue;
    q.set(klucz, String(wartosc));
  }
  const tekst = q.toString();
  return tekst ? `?${tekst}` : "";
}

export interface ProsodySummaryDto {
  durationSec: number;
  phonationSec: number;
  syllablesPerSecond: number | null;
  wordsPerMinute: number | null;
  articulationRate: number | null;
  pauseCount: number;
  pauseRatio: number;
  longestPauseMs: number;
  rmsDbMean: number | null;
  f0MedianHz: number | null;
  f0RangeSemitones: number | null;
  voicedRatio: number;
  flags: string[];
}

export interface MicShotResponse {
  id: string | null;
  persisted: boolean;
  text: string;
  wordCount: number;
  emotion: string | null;
  emotionLabel: string | null;
  emotionFunction: string | null;
  /**
   * Liczbowa ocena ekspresji z modelu. Zapisywana, ale SWIADOMIE NIE
   * pokazywana w interfejsie - pomiar opisany w docs/eksperyment-emocje.md
   * wykazal, ze przy temperaturze 0 model daje te sama wartosc wszystkiemu,
   * a przy 1 rozrzut jest wiekszy niz sygnal.
   */
  expression: number | null;
  confidence: string | null;
  vocalCues: string | null;
  /**
   * Czy opis brzmienia da sie pogodzic z pomiarem obok. null znaczy "nie bylo
   * czego sprawdzac" i jest czestym, normalnym przypadkiem. Przy false pole
   * vocalCues jest puste, a powod stoi w vocalCuesConflict.
   */
  vocalCuesVerified: boolean | null;
  vocalCuesConflict: string | null;
  /**
   * Nazwy modeli, ktore faktycznie wykonaly te dwa wywolania. Pokazywane
   * w dymku przy znaczniku "model". emotionModel jest null, gdy ocena
   * brzmienia sie nie powiodla.
   */
  transcriptModel: string | null;
  emotionModel: string | null;
  /**
   * Wersja ZAMOWIONA, ustawiana tylko przy rozbieznosci z uzyta. null znaczy
   * "zamowiono to, czym policzono", czyli przypadek normalny.
   */
  transcriptModelRequested: string | null;
  emotionModelRequested: string | null;
  /** Ile prob poszlo do dostawcy ekspresji. Wiecej niz jedna znaczy ponowienie. */
  emotionProby: number | null;
  /** Identyfikator proby, ten sam co MicShot.id. Po nim sklada sie paczka audytu. */
  shotId: string;
  /** Czy nagranie udalo sie zapisac na dysku. Falsz znaczy brak miejsca albo blad zapisu. */
  audioZapisane: boolean;
  /**
   * Ustawione, gdy dostawca ekspresji nie odpowiedzial. Odroznia awarie modelu
   * od wypowiedzi, w ktorej nie bylo czego ocenic - na ekranie to sa dwa rozne
   * komunikaty, bo to sa dwie rozne rzeczy.
   */
  emotionError: string | null;
  /**
   * Dostawca, ktory FAKTYCZNIE policzyl wynik, i - gdy sie roznia - ten
   * zamowiony w ustawieniach. Ekran ma dawac sie zestawic z panelem bez
   * wchodzenia w logi.
   */
  transcriptProvider: string | null;
  emotionProvider: string | null;
  transcriptFallbackFrom: string | null;
  emotionFallbackFrom: string | null;
  /**
   * WARSTWA SYGNALOWA - trzy liczby z modelu, ktory nie zna slow.
   *
   * `serStan` ma TRZY wartosci, nie dwie, i to jest sedno: "wczytywanie" to nie
   * awaria, tylko model wstajacy do pamieci. Sidecar trzyma jeden model naraz,
   * wiec pierwsze nagranie po zmianie modelu trafia wlasnie na to.
   */
  serArousal: number | null;
  serValence: number | null;
  serDominance: number | null;
  serModel: string | null;
  serError: string | null;
  serStan: string | null;
  serSzacowanyCzasS: number | null;
  prosody: ProsodySummaryDto;
  paceLabel: string | null;
  paceHint: string | null;
  toneLabel: string | null;
  toneHint: string | null;
  latencyMs: number;
}

/**
 * Ten sam bajt audio przez wszystkich dostawcow.
 *
 * `pomiar` stoi osobno i raz, bo jest arbitrem dla calej tabeli - te same
 * liczby dla kazdego wiersza. `audioSha256` jest po to, zeby dalo sie
 * sprawdzic, ze kazdy dostawca faktycznie dostal to samo.
 */
export interface PorownanieDto {
  shotId: string;
  operationId: string;
  pomiar: {
    syllablesPerSecond: number | null;
    paceLabel: string | null;
    f0RangeSemitones: number | null;
    toneLabel: string | null;
  };
  audioSha256: string | null;
  wyniki: {
    provider: string;
    label: string;
    model: string | null;
    modelRequested: string | null;
    modelUsed: string | null;
    emotion: string | null;
    emotionLabel: string | null;
    vocalCues: string | null;
    vocalCuesVerified: boolean | null;
    vocalCuesConflict: string | null;
    latencyMs: number | null;
    proby: number | null;
    ok: boolean;
    error: string | null;
  }[];
}

export interface MicHistoryEntry {
  id: string;
  createdAt: string;
  text: string;
  /** Slug z taksonomii - klucz, nie tekst dla czlowieka. Do wyswietlenia sluzy emotionLabel. */
  emotion: string | null;
  /** Etykieta z metodyki, np. "Stanowczość lub lekka irytacja". null, gdy emocji nie ustalono. */
  emotionLabel: string | null;
  intensity: number | null;
  /** Wymiary z sygnalu. Zrodlo trendu pobudzenia miedzy dwiema probami. */
  serArousal: number | null;
  serValence: number | null;
  serDominance: number | null;
  syllablesPerSecond: number | null;
  f0MedianHz: number | null;
  f0RangeSemitones: number | null;
  paceLabel: string | null;
  toneLabel: string | null;
  qualityFlags: string | null;
  /**
   * Czy nagranie tej proby jeszcze lezy na dysku.
   *
   * Jedna flaga zamiast dwoch dat: ekran ma odpowiedziec na pytanie "czy jest
   * czego posluchac", a nie prowadzic wlasna arytmetyke retencji. Falsz przy
   * probach sprzed 19.08.2026 (audio wtedy nie zostawalo) i po 30 dniach.
   */
  maAudio: boolean;
}

export interface ProviderInfoDto {
  id: string;
  label: string;
  kind: "stt" | "expression" | "ser";
  /** Wersja domyslna dostawcy. */
  model: string;
  /** Wersje do wyboru, pobrane z API dostawcy. Pusta dla dostawcy bez klucza. */
  models: string[];
  available: boolean;
  unavailableReason: string | null;
  note: string;
  /**
   * Opisy wersji, gdy sama nazwa nie wystarcza do wyboru. Dzis wypelnia to
   * wylacznie warstwa sygnalowa - licencja modelu rozstrzyga, czy wolno go
   * uzywac komercyjnie, a z nazwy tego nie widac.
   */
  modelInfo?: {
    id: string;
    license: string;
    commercialUse: boolean;
    note: string;
    /** Najdluzsze nagranie, jakie ten model przyjmie. null = sidecar nie podal. */
    maxSeconds: number | null;
  }[];
}

export interface ProviderLists {
  stt: ProviderInfoDto[];
  expression: ProviderInfoDto[];
  ser: ProviderInfoDto[];
}

export interface SettingsDto {
  sttProvider: string;
  expressionProvider: string;
  /** Warstwa sygnalowa. "brak" znaczy: nie licz wymiarow. */
  serProvider: string;
  /** Null znaczy "domyslna wersja dostawcy". */
  sttModel: string | null;
  expressionModel: string | null;
  serModel: string | null;
  /**
   * Model OpenAI do oceny wg metodyki, podzialu tekstu i nazw mowcow. Null znaczy "bierz
   * domyslny ze srodowiska serwera" (TextModelDto.model). Opcjonalne: starszy serwer go nie zna.
   */
  textModel?: string | null;
  lensConcurrency: number;
  defaultAudioRetention: string;
  micMaxSeconds: number;
  /** ISO albo null. Do kiedy otwarte jest okno narzedzi diagnostycznych. */
  devToolsEnabledUntil: string | null;
  /** Policzone na serwerze: czy okno jest otwarte TERAZ. Zaznaczone != dzialajace. */
  devToolsEnabled: boolean;
  updatedAt: string;
}

/** Opis modelu tekstowego z GET /api/settings (osobny od dostawcow: to jeden model, nie wybor dostawcy). */
export interface TextModelDto {
  /** Model ze srodowiska serwera: uzywany, gdy ustawienia nie wybieraja innego. */
  model: string;
  /** Podpowiedzi z API dostawcy. Pusta, gdy brak klucza. */
  models: string[];
  available: boolean;
  unavailableReason: string | null;
  note: string;
}

export interface SessionListEntry {
  id: string;
  title: string;
  status: string;
  /** AUDIO (nagranie) albo TEXT (tekst wgrany albo wklejony). */
  inputKind: InputKind;
  durationSec: number | null;
  healthStatus: string | null;
  healthFlags: string | null;
  createdAt: string;
  error: string | null;
  utteranceCount: number;
  speakerCount: number;
  /**
   * True, gdy sesja ma co najmniej jeden udany przebieg soczewki. Pole opcjonalne: brak (starszy serwer) to "nie
   * wiadomo", nie "nie ma oceny". Uzywa go okno "Link do recenzji".
   */
  maOcene?: boolean;
  /**
   * Tylko u administratora: true, gdy to rozmowa konta uzytkownika (sam fakt, loginu lista nie podaje). Okno "Link do
   * recenzji" pokazuje ja wylaczona. Brak pola (konto user, starszy serwer) to "nie wiadomo": okno nic nie blokuje, a
   * ostatnie slowo ma serwer (400 rozmowa_konta_uzytkownika przy tworzeniu linku).
   */
  zKontaUzytkownika?: boolean;
}

/**
 * Adnotacja ma dwie polowy i one nie sa rownowazne: measured to pomiar,
 * interpreted to opinia modelu. Interfejs ma to rozroznienie pokazac, a nie
 * skleic w jeden nierozroznialny napis.
 */
export interface AnnotationDto {
  measured: string[];
  interpreted: string | null;
  /**
   * Ustawione, gdy opis modelu PRZECZYL pomiarowi i przez to nie trafil do
   * interpreted. Zdanie cytuje opis, wiec tresc modelu nie ginie z ekranu -
   * przestaje tylko byc podawana jako wynik.
   */
  interpretedConflict: string | null;
}

export interface UtteranceDto {
  index: number;
  /** Speaker.id (speakers[].id w SessionDetail): do zmiany mowcy przy wypowiedzi. */
  speakerId: string;
  /** Dla tekstu bez czasow (SessionDetail.hasTimestamps = false) to 0, a nie czas. */
  startMs: number;
  endMs: number | null;
  text: string;
  speaker: string;
  annotation: AnnotationDto;
  emotions: { provider: string; model: string; emotion: string | null; delivery: string | null; confidence: string | null }[];
  qualityFlags: string[];
}

export interface ConversationSummaryDto {
  /**
   * Skad udzial w rozmowie: "time" (nagranie, z granic wypowiedzi) albo "words" (tekst, ze slow).
   * Przy "words" talkSec i avgTurnSec sa null, a words i avgTurnWords sa podane.
   */
  basis: "time" | "words";
  speakers: {
    speaker: string;
    talkSec: number | null;
    share: number;
    turns: number;
    avgTurnSec: number | null;
    words?: number;
    avgTurnWords?: number;
  }[];
  turns: number;
  /** Null dla tekstu (i gdy za malo danych). */
  medianResponseGapMs: number | null;
  /** Null dla tekstu: wejsc w slowo nie da sie zmierzyc bez nagrania. */
  interruptions: number | null;
}

/**
 * Wskazanie z soczewki - fragment do poprawy plus propozycja lepszego zdania.
 *
 * quoteVerified === false znaczy, ze cytatu NIE znaleziono w wypowiedzi.
 * Taki cytat wolno pokazac, ale nie wolno podswietlic w transkrypcie - to jest
 * zabezpieczenie przed halucynacja, a nie szczegol wizualny.
 *
 * Wypowiedz wskazuje utteranceIndex, czyli pozycja w rozmowie. Identyfikatory
 * z bazy nie wychodza do przegladarki.
 */
export interface LensFindingDto {
  id: string;
  utteranceIndex: number | null;
  blockKey: string;
  severity: string;
  /** Identyfikator znacznika z metodyki, np. "dopasowanie.tak-ale". */
  category: string | null;
  /** mocna-strona albo do-poprawy. */
  kind: FindingKind;
  quote: string | null;
  quoteStart: number | null;
  quoteEnd: number | null;
  quoteVerified: boolean;
  /** Przy do-poprawy: pierwszy wariant (to samo co variants[0].tekst). */
  suggestion: string | null;
  /** Przy mocnej stronie: dlaczego to dobre. Przy do-poprawy: jak i czym mowca obniza wplyw. */
  rationale: string | null;
  /** Tylko mocna strona: jak to rozwijac. */
  howToDevelop: string | null;
  /** Tylko do-poprawy: 1-3 lepsze wersje wypowiedzi z uzasadnieniem. */
  variants: WariantWskazaniaDto[] | null;
  orderIndex: number;
}

export interface WariantWskazaniaDto {
  tekst: string;
  dlaczegoLepiej: string;
}

/**
 * Jedno uruchomienie jednej soczewki. promptText i rawResponse celowo NIE sa
 * czescia kontraktu - zostaja na serwerze, bo to kilkadziesiat kilobajtow na
 * przebieg, po ktore interfejs i tak nigdy nie siega.
 */
export interface LensRunDto {
  id: string;
  /** Zadanie, ktore wywolalo przebieg. Null przy przebiegach sprzed wyboru soczewek. */
  jobId: string | null;
  lensId: string;
  lensSlug: string;
  /** Tytul soczewki po polsku, w wersji metodyki, ktora policzyla ten przebieg. */
  lensTitle: string;
  /**
   * Cwiczenie pod raportem (staly tekst z metodyki, bez modelu). Tylko gdy przebieg ma wskazania
   * do poprawy i wersja metodyki jest znana; inaczej null.
   */
  exercise: string | null;
  lensHash: string;
  methodologyVersion: string;
  status: string;
  provider: string;
  model: string;
  resultJson: unknown;
  latencyMs: number | null;
  retries: number;
  error: string | null;
  createdAt: string;
  findings: LensFindingDto[];
}

/** Soczewka z wyboru zadania: slug i tytul po polsku (z biezacej metodyki). */
export interface WyborSoczewkiDto {
  slug: string;
  title: string;
}

/**
 * Ostatnie zadanie, ktore ocenia soczewkami. Soczewka z wyboru bez przebiegu w lensRuns jest "w
 * toku" (status PENDING albo RUNNING) albo "nie policzona" (DONE albo FAILED, np. awaria zapisu).
 */
export interface OcenaZadaniaDto {
  jobId: string;
  /** FULL_ANALYSIS | TEXT_ANALYSIS | SPLIT_LENSES | RERUN_LENSES. */
  kind: string;
  status: "PENDING" | "RUNNING" | "DONE" | "FAILED";
  /** Powod porazki albo ostrzezenie o awarii zapisu wynikow. Null, gdy wszystko poszlo. */
  error: string | null;
  createdAt: string;
  updatedAt: string;
  soczewki: WyborSoczewkiDto[];
}

/** Mowca sesji. Nazwa do pokazania to `displayName ?? label`. */
export interface MowcaDto {
  id: string;
  label: string;
  displayName: string | null;
  /** SPRZEDAWCA | KLIENT | INNY. Domyslnie INNY, dopoki nic go nie ustawi. */
  role: string;
  utteranceCount: number;
}

export interface SessionDetail {
  summary: ConversationSummaryDto;
  id: string;
  title: string;
  status: string;
  /** AUDIO (nagranie) albo TEXT (tekst wgrany albo wklejony). */
  inputKind: InputKind;
  /** Opis rozmowy od uzytkownika. */
  context: string | null;
  /** False: czasy wypowiedzi nie istnieja (startMs to 0). Interfejs ich nie pokazuje. */
  hasTimestamps: boolean;
  /** "AI", gdy mowcow do wypowiedzi przypisal model (tekst bez podpisow, zatwierdzony przez uzytkownika). */
  speakersAssignedBy: string | null;
  /**
   * Speaker.id mowcy, ktory jest uzytkownikiem ("Kim jestes w tej rozmowie", shared/jaTypy.ts), albo null, gdy nie
   * wybrano. Opcjonalne: starszy serwer go nie zna, a to samo znaczy brak pola i null.
   */
  userSpeakerId?: string | null;
  /**
   * Wlasciciel rozmowy (AnalysisSession.ownerUsername), tylko u administratora: u konta user serwer wycina to pole
   * (widokDlaKonta.ts). Napis to rozmowa konta uzytkownika, null to rozmowa administratora, brak pola to "nie wiadomo".
   */
  ownerUsername?: string | null;
  /** Wersja metodyki z ostatniej oceny. */
  methodologyVersion: string | null;
  /** Ostatnie zadanie oceny soczewkami. Null, gdy sesja nigdy nie byla oceniana. */
  ocena: OcenaZadaniaDto | null;
  /** Tylko w statusie SPLIT_REVIEW: wybor soczewek z chwili wgrania, do podpowiedzi przy zatwierdzaniu. */
  podzial: { soczewki: WyborSoczewkiDto[] } | null;
  durationSec: number | null;
  wordCount: number | null;
  healthStatus: string | null;
  healthFlags: string | null;
  error: string | null;
  createdAt: string;
  sttProvider: string | null;
  sttModel: string | null;
  expressionProvider: string | null;
  expressionModel: string | null;
  /**
   * mimeType jest potrzebny odtwarzaczowi, zeby wiedziec, czy przegladarka w
   * ogole umie zagrac ten plik. Serwer wysylal go od poczatku, gubil dopiero
   * ten typ.
   */
  audio: {
    originalName: string;
    mimeType: string;
    bytes: number;
    durationSec: number | null;
    deletedAt: string | null;
  } | null;
  /** role: SPRZEDAWCA | KLIENT | INNY. Domyslnie INNY, dopoki nic go nie ustawi. */
  speakers: MowcaDto[];
  utterances: UtteranceDto[];
  lensRuns: LensRunDto[];
  /**
   * Koszt analizy z dziennika wywolan (szacunek wg cennika). Opcjonalne: starszy serwer go nie zwraca, a blad liczenia
   * wraca jako `{ stan: "blad" }`, nie psuje reszty odpowiedzi.
   */
  koszt?: KosztSesjiDto;
}

/** Odpowiedz GET i PUT /api/settings. `textModel` opcjonalny: starszy serwer go nie zwraca. */
export interface UstawieniaResponse {
  settings: SettingsDto;
  providers: ProviderLists;
  textModel?: TextModelDto;
}

/**
 * Parametr oceny do wyboru przy wgrywaniu i przy ponownej ocenie (GET /api/sessions/meta/lenses).
 * W interfejsie mowimy "parametry"; "soczewka" to wewnetrzna nazwa z serwera i metodyki.
 */
export interface ParametrOceny {
  slug: string;
  title: string;
  /** Jedno zdanie o tym, co parametr ocenia. */
  coOceniamy: string;
  domyslnieZaznaczona: boolean;
}

export interface KatalogParametrowDto {
  /** Wersja metodyki, z ktorej pochodzi lista. */
  wersja: string;
  /** Nazwa pola jak na drucie (serwer mowi "soczewki"); w interfejsie to "parametry". */
  soczewki: ParametrOceny[];
}

/** Format pliku z raportem oceny: Word (do dalszej pracy) albo PDF (do czytania i wysylania). */
export type FormatRaportu = "docx" | "pdf";

export type RolaMowcy = "SPRZEDAWCA" | "KLIENT" | "INNY";

/** Pola wspolne wejscia, niezaleznie od tego, czy to nagranie, plik tekstowy czy wklejony tekst. */
interface WejscieWspolne {
  /** Pusty tytul znaczy: serwer bierze nazwe pliku albo nazwe domyslna. */
  title: string;
  /** Opis rozmowy. Pusty (po przycieciu) nie jest wysylany. */
  opis: string;
  /** Prawdziwy wybor uzytkownika. Serwer odrzuca wszystko poza "true". */
  zgoda: boolean;
  /** Slugi wybranych parametrow oceny (z GET /api/sessions/meta/lenses). */
  soczewki: readonly string[];
  /**
   * "Kim jestes w tej rozmowie": nazwa mowcy z tekstu Z PODPISAMI, ktory jest uzytkownikiem (pole formularza
   * `selfSpeaker`, shared/jaTypy.ts). Tylko dla tekstu; przy nagraniu i tekscie bez podpisow mowcow jeszcze nie ma,
   * wiec wybor zapada na ekranie wyniku. Pusta wartosc nie jest wysylana.
   */
  mowcaUzytkownika?: string;
}

export interface WejscieNagrania extends WejscieWspolne {
  rodzaj: "AUDIO";
  file: File;
  audioRetention: string;
  sttProvider: string;
  expressionProvider: string;
  sttModel: string;
  expressionModel: string;
}

/** Tekst: albo plik .txt (`plikTekstu`), albo wklejony tekst (`tekst`). Nigdy oba naraz. */
export interface WejscieTekstu extends WejscieWspolne {
  rodzaj: "TEXT";
  plikTekstu?: File;
  tekst?: string;
}

/** 202 z POST /api/sessions. Pola tekstu sa puste przy nagraniu, pola nagrania przy tekscie. */
export interface OdpowiedzPrzyjecia {
  id: string;
  title: string;
  status: string;
  durationSec?: number | null;
  bytes?: number;
  /** Tekst: true - mowcy i wypowiedzi sa juz w bazie; false - tekst czeka na podzial przez model. */
  podpisany?: boolean;
  liczbaMowcow?: number | null;
  liczbaWypowiedzi?: number | null;
}

/** GET /api/sessions/meta/limity: limity nagran widziane przez serwer (zrodlem prawdy jest jego konfiguracja). */
export interface LimityWgrywaniaDto {
  maxBajtowNagrania: number;
  maxCzasuNagraniaSek: number;
  rozmiarPorcjiBajtow: number;
  /** Plik nie wiekszy niz to jedzie jednym zadaniem multipart (stara sciezka), wiekszy porcjami. */
  maxBajtowJednegoZadania: number;
}

/** Cialo POST /api/sessions/uploads: te same pola i te same walidacje co multipart dla nagrania. */
export interface PolaStartuWgrywania {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  title?: string;
  context?: string;
  consent: boolean;
  audioRetention?: string;
  lensSlugs?: string[];
  sttProvider?: string;
  expressionProvider?: string;
  sttModel?: string;
  expressionModel?: string;
}

/** 201 z POST /api/sessions/uploads. */
export interface OdpowiedzStartuWgrywania {
  uploadId: string;
  chunkBytes: number;
  totalChunks: number;
  expiresAt: string;
}

/** 200 z GET /api/sessions/uploads/:id: stan z dysku serwera, jedyne wiarygodne zrodlo przy wznowieniu. */
export interface StanWgrywaniaDto {
  uploadId: string;
  sizeBytes: number;
  chunkBytes: number;
  totalChunks: number;
  receivedBytes: number;
  nextIndex: number;
}

/** 200 z PUT /api/sessions/uploads/:id/chunks/:index. */
export interface OdpowiedzPorcjiDto {
  receivedBytes: number;
  nextIndex: number;
}

/** 202 z POST /api/sessions/:id/ocena i /podzial/zatwierdz. */
export interface OdpowiedzZlecenia {
  id: string;
  status: string;
  jobId: string;
  lensSlugs: string[];
}

/** Zmiana jednej wypowiedzi przy zatwierdzaniu podzialu: `index` od 0 jak w utterances[].index. */
export interface PoprawkaWypowiedzi {
  index: number;
  speakerId: string;
}

/** Zmiana nazwy albo roli mowcy. `displayName: null` kasuje nazwe wlasna (wraca etykieta). */
export interface PoprawkaMowcy {
  displayName?: string | null;
  role?: RolaMowcy;
}

export interface ZatwierdzeniePodzialu {
  /** Pominiete: serwer bierze wybor z chwili wgrania. */
  lensSlugs?: readonly string[];
  wypowiedzi?: readonly PoprawkaWypowiedzi[];
  mowcy?: readonly (PoprawkaMowcy & { id: string })[];
}

/**
 * Lista z odpowiedzi panelu recenzji: gola tablica albo obiekt z lista pod nazwanym polem. Ksztalt koperty nie jest
 * czescia kontraktu w shared/, wiec klient przyjmuje oba, a cokolwiek innego to blad odpowiedzi, nie pusta lista.
 */
function listaZOdpowiedzi<T>(odpowiedz: unknown, pole: string): T[] {
  if (Array.isArray(odpowiedz)) return odpowiedz as T[];
  const wartosc = odpowiedz !== null && typeof odpowiedz === "object" ? (odpowiedz as Record<string, unknown>)[pole] : undefined;
  if (Array.isArray(wartosc)) return wartosc as T[];
  throw new ApiError(502, "zla_odpowiedz");
}

/** Pojedynczy link z odpowiedzi: sam obiekt albo obiekt w polu `link`. */
function linkZOdpowiedzi(odpowiedz: unknown): LinkRecenzjiAdmin {
  const wartosc =
    odpowiedz !== null && typeof odpowiedz === "object" && "link" in odpowiedz ? (odpowiedz as { link: unknown }).link : odpowiedz;
  if (wartosc !== null && typeof wartosc === "object" && typeof (wartosc as { token?: unknown }).token === "string") {
    return wartosc as LinkRecenzjiAdmin;
  }
  throw new ApiError(502, "zla_odpowiedz");
}

export const api = {
  me: () => request<OdpowiedzKonta>("/api/auth/me"),

  login: (username: string, password: string) =>
    request<OdpowiedzKonta>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),

  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  micAnalyze: async (
    blob: Blob,
    mimeType: string,
    durationMs: number,
    /** Pozwala uzytkownikowi przerwac czekanie. Bez tego jedyna droga to przeladowanie strony. */
    signal?: AbortSignal,
  ): Promise<MicShotResponse> => {
    const form = new FormData();
    // Rozszerzenie nie ma znaczenia dla serwera, ale ulatwia diagnostyke w logach.
    form.append("audio", blob, `shot.${mimeType.includes("mp4") ? "mp4" : "webm"}`);
    form.append("durationMs", String(durationMs));

    const res = await fetch("/api/mic/analyze", {
      method: "POST",
      credentials: "same-origin",
      body: form,
      signal,
    });
    return handle<MicShotResponse>(res);
  },

  micHistory: (limit = 5) =>
    request<{ shots: MicHistoryEntry[]; unavailable?: boolean }>(`/api/mic/history?limit=${limit}`),

  /** Ilu dostawcow wejdzie do porownania. Potrzebne, zeby pytanie o zgode podalo liczbe wywolan. */
  micDostawcyPorownania: () =>
    request<{ dostawcy: { id: string; label: string; model: string }[] }>("/api/mic/porownanie/dostawcy"),

  micPorownaj: (id: string) =>
    request<PorownanieDto>(`/api/mic/${id}/porownaj`, { method: "POST" }),

  settings: () => request<UstawieniaResponse>("/api/settings"),

  saveSettings: (patch: {
    sttProvider?: string;
    expressionProvider?: string;
    serProvider?: string;
    sttModel?: string | null;
    expressionModel?: string | null;
    serModel?: string | null;
    textModel?: string | null;
    defaultAudioRetention?: string;
    micMaxSeconds?: number;
    lensConcurrency?: number;
    devToolsEnabled?: boolean;
  }) =>
    request<UstawieniaResponse>("/api/settings", {
      method: "PUT",
      body: JSON.stringify(patch),
    }),

  /**
   * Przeladowanie metodyki z dysku, bez restartu serwera.
   *
   * Zwraca liste emocji, a nie sama wersje: administrator ma zobaczyc, ze nowa
   * pozycja faktycznie doszla. Zly plik wraca jako 400 i NIE podmienia metodyki,
   * ktora dziala.
   */
  przeladujMetodyke: () =>
    request<{
      wersja: string;
      emocje: string[];
      /** Soczewki wczytane z methodology/lenses/ (opcjonalne: starszy serwer ich nie zwraca). */
      soczewki?: { slug: string; title: string; enabled: boolean }[];
    }>("/api/settings/metodyka/przeladuj", {
      method: "POST",
    }),

  providers: () => request<ProviderLists>("/api/sessions/meta/providers"),

  sessions: (limit = 50) => request<{ sessions: SessionListEntry[] }>(`/api/sessions?limit=${limit}`),

  session: (id: string) => request<SessionDetail>(`/api/sessions/${id}`),

  /**
   * Katalog parametrow oceny do pol wyboru: tylko wlaczone, w kolejnosci z metodyki.
   * Serwer przyjmie w `lensSlugs` wylacznie slugi z tej listy.
   */
  parametryOceny: () => request<KatalogParametrowDto>("/api/sessions/meta/lenses"),

  /** Zestawienie kosztow modeli za okres (GET /api/koszty). Szacunek wg cennika, nie faktura. */
  koszty: (dni: OkresDni, zrodlo: ZrodloKosztow) =>
    request<KosztyOkresuDto>(`/api/koszty?${new URLSearchParams({ dni: String(dni), zrodlo }).toString()}`),

  /**
   * Zakladka Developer. Wszystko pod /api/developer (za logowaniem). Parametry puste lub niezdefiniowane nie ida do
   * adresu: serwer traktuje brak jako "bez filtra".
   */
  developerWywolania: (parametry: Record<string, string | number | undefined>) =>
    request<ListaWywolanDto>(`/api/developer/wywolania${zapytanie(parametry)}`),
  developerWywolanie: (id: string) =>
    request<WywolanieSzczegolDto>(`/api/developer/wywolania/${encodeURIComponent(id)}`),
  developerTresc: (id: string, czesc: CzescTresci, offset: number, dlugosc: number) =>
    request<TrescWywolaniaDto>(
      `/api/developer/wywolania/${encodeURIComponent(id)}/tresc${zapytanie({ czesc, offset, dlugosc })}`,
    ),
  developerStan: () => request<StanSystemuDto>("/api/developer/stan"),
  developerLogi: (parametry: Record<string, string | number | undefined>) =>
    request<LogiDto>(`/api/developer/logi${zapytanie(parametry)}`),
  developerSondaPodglad: () => request<PodgladSondyDto>("/api/developer/sonda"),
  /** Sonda dostawcow jest PLATNA: serwer wymaga jawnego potwierdzenia w tresci zadania. */
  developerSonda: () =>
    request<WynikSondyDto>("/api/developer/sonda", { method: "POST", body: JSON.stringify({ potwierdzam: true }) }),

  /**
   * Przyjecie nowej analizy: nagranie, plik tekstowy albo wklejony tekst (multipart/form-data).
   * Kazde wejscie niesie opis rozmowy, prawdziwa zgode i wybor parametrow oceny.
   */
  upload: async (
    params: WejscieNagrania | WejscieTekstu,
    /** Pozwala przerwac wysylke nagrania (przycisk "Anuluj wysylanie"); bez tego zadanie dochodzi do konca. */
    opcje?: { signal?: AbortSignal },
  ): Promise<OdpowiedzPrzyjecia> => {
    const form = new FormData();
    form.append("inputKind", params.rodzaj);
    if (params.title.trim()) form.append("title", params.title.trim());
    // Zgoda jest wymagana przez API, nie tylko zaznaczana w interfejsie: wysylamy to, co
    // uzytkownik wybral, a serwer odrzuca wszystko poza "true".
    form.append("consent", params.zgoda ? "true" : "false");
    const opis = params.opis.trim();
    if (opis) form.append("context", opis);
    for (const slug of params.soczewki) form.append("lensSlugs", slug);
    const mowcaUzytkownika = params.mowcaUzytkownika?.trim();
    if (mowcaUzytkownika) form.append(POLE_MOWCA_UZYTKOWNIKA, mowcaUzytkownika);

    if (params.rodzaj === "AUDIO") {
      form.append("audio", params.file, params.file.name);
      form.append("audioRetention", params.audioRetention);
      if (params.sttProvider) form.append("sttProvider", params.sttProvider);
      if (params.expressionProvider) form.append("expressionProvider", params.expressionProvider);
      if (params.sttModel) form.append("sttModel", params.sttModel);
      if (params.expressionModel) form.append("expressionModel", params.expressionModel);
    } else if (params.plikTekstu) {
      form.append("transcript", params.plikTekstu, params.plikTekstu.name);
    } else {
      form.append("text", params.tekst ?? "");
    }

    const res = await fetch("/api/sessions", {
      method: "POST",
      credentials: "same-origin",
      body: form,
      signal: opcje?.signal,
    });
    return handle<OdpowiedzPrzyjecia>(res);
  },

  /**
   * Limity nagran (GET /api/sessions/meta/limity). Starszy serwer nie ma tej trasy: dostaje 404, a czasem odpowiedz
   * 200 bez JSON-a (strona aplikacji zamiast API) - jedno i drugie znaczy "brak endpointu", wiec zwracamy 404, a o
   * dalszym zachowaniu decyduje lib/wgrywanie.ts.
   */
  limityWgrywania: async (signal?: AbortSignal): Promise<LimityWgrywaniaDto> => {
    const res = await fetch("/api/sessions/meta/limity", { credentials: "same-origin", signal });
    if (!res.ok) throw await bladZOdpowiedzi(res);
    try {
      return (await res.json()) as LimityWgrywaniaDto;
    } catch {
      throw new ApiError(404, "brak_endpointu");
    }
  },

  /**
   * Wgrywanie duzego nagrania porcjami (POST/GET/PUT/DELETE pod /api/sessions/uploads). Kolejnosc wywolan i polityke
   * ponowien trzyma lib/wgrywanie.ts; tu jest tylko transport.
   */
  wgrywanieStart: (pola: PolaStartuWgrywania, signal?: AbortSignal) =>
    request<OdpowiedzStartuWgrywania>("/api/sessions/uploads", {
      method: "POST",
      body: JSON.stringify(pola),
      signal,
    }),

  wgrywanieStan: (uploadId: string, signal?: AbortSignal) =>
    request<StanWgrywaniaDto>(`/api/sessions/uploads/${encodeURIComponent(uploadId)}`, { signal }),

  /**
   * Jedna porcja jako surowe cialo (bez multipart, bez JSON-a). Content-Length ustawia przegladarka z rozmiaru Blob,
   * a serwer wymaga dokladnie oczekiwanej dlugosci.
   */
  wgrywaniePorcja: async (
    uploadId: string,
    index: number,
    dane: Blob,
    signal?: AbortSignal,
  ): Promise<OdpowiedzPorcjiDto> => {
    const res = await fetch(`/api/sessions/uploads/${encodeURIComponent(uploadId)}/chunks/${index}`, {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/octet-stream" },
      body: dane,
      signal,
    });
    return handle<OdpowiedzPorcjiDto>(res);
  },

  /** Zlozenie nagrania z porcji i zalozenie analizy (202, ten sam ksztalt co POST /api/sessions). */
  wgrywanieKoniec: (uploadId: string) =>
    request<OdpowiedzPrzyjecia>(`/api/sessions/uploads/${encodeURIComponent(uploadId)}/complete`, {
      method: "POST",
    }),

  /** Porzucenie wgrywania (204, idempotentne). Nie ma ciala odpowiedzi, wiec bez `handle`. */
  wgrywanieAnuluj: async (uploadId: string): Promise<void> => {
    const res = await fetch(`/api/sessions/uploads/${encodeURIComponent(uploadId)}`, {
      method: "DELETE",
      credentials: "same-origin",
    });
    if (!res.ok) throw await bladZOdpowiedzi(res);
  },

  /**
   * "Oceń ponownie": nowe zadanie oceny na tym samym transkrypcie. Sesja przechodzi od razu
   * na status LENSES, wiec po odpowiedzi trzeba pobrac sesje jeszcze raz (odpytywanie ruszy samo).
   */
  ocenPonownie: (id: string, lensSlugs: readonly string[]) =>
    request<OdpowiedzZlecenia>(`/api/sessions/${id}/ocena`, {
      method: "POST",
      body: JSON.stringify({ lensSlugs }),
    }),

  /**
   * Zatwierdzenie podzialu tekstu (status SPLIT_REVIEW): poprawki przypisan, nazw i rol oraz
   * zlecenie oceny, wszystko w jednej transakcji na serwerze.
   */
  zatwierdzPodzial: (id: string, body: ZatwierdzeniePodzialu) =>
    request<OdpowiedzZlecenia>(`/api/sessions/${id}/podzial/zatwierdz`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /**
   * Raport z oceny jako plik Word albo PDF (GET /api/sessions/:id/raport.docx | raport.pdf). Plik obejmuje WSZYSTKICH
   * mowcow, niezaleznie od mowcy wybranego na ekranie. Pobieranie przez fetch zamiast zwyklego linku, zeby przycisk
   * mial stan ladowania, a odmowy serwera (409: ocena w toku, brak oceny) wrocily jako zdania, a nie jako strona z
   * JSON-em.
   */
  pobierzRaport: async (id: string, format: FormatRaportu = "docx"): Promise<{ blob: Blob; nazwaPliku: string }> => {
    const res = await fetch(`/api/sessions/${id}/raport.${format}`, { credentials: "same-origin" });
    if (!res.ok) throw await bladZOdpowiedzi(res);
    const blob = await res.blob();
    return { blob, nazwaPliku: nazwaPlikuZNaglowka(res.headers.get("Content-Disposition"), format) };
  },

  /**
   * "Kim jestes w tej rozmowie": zapis mowcy, ktorego dotyczy wynik uzytkownika (PUT /api/sessions/:id/ja). `null`
   * zdejmuje wybor. Nie zmienia statusu ani oceny: to tylko wskazanie, czyj wynik pokazac.
   */
  ustawMowceUzytkownika: (id: string, speakerId: string | null) => {
    const cialo: ZapisMowcyUzytkownikaDto = { speakerId };
    return request<OdpowiedzMowcyUzytkownikaDto>(`/api/sessions/${encodeURIComponent(id)}/ja`, {
      method: "PUT",
      body: JSON.stringify(cialo),
    });
  },

  /** Zmiana nazwy i/lub roli mowcy. Nie wymaga ponownej oceny i nie zmienia statusu sesji. */
  zmienMowce: (id: string, speakerId: string, patch: PoprawkaMowcy) =>
    request<{ id: string; label: string; displayName: string | null; role: RolaMowcy }>(
      `/api/sessions/${id}/speakers/${speakerId}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    ),

  // --- Panel "Recenzje" (tylko administrator): /api/recenzje/*. Kontrakt typow: shared/recenzjeTypy.ts. ---

  /** Wszystkie linki dla recenzentow, takze wygasle i uniewaznione, z postepem. */
  recenzjeLinki: async (): Promise<LinkRecenzjiAdmin[]> =>
    listaZOdpowiedzi<LinkRecenzjiAdmin>(await request<unknown>("/api/recenzje/linki"), "linki"),

  /** Nowy link. Odpowiedz niesie token, z ktorego klient sklada adres (sciezka z shared/recenzjeTypy.ts). */
  recenzjeUtworzLink: async (wejscie: UtworzLinkWejscie): Promise<LinkRecenzjiAdmin> =>
    linkZOdpowiedzi(
      await request<unknown>("/api/recenzje/linki", { method: "POST", body: JSON.stringify(wejscie) }),
    ),

  /** Uniewaznienie linku: od tej chwili token daje recenzentowi 404. Zapisane uwagi zostaja. */
  recenzjeUniewaznijLink: async (id: string): Promise<void> => {
    // Bez res.json(): sukces moze przyjsc bez tresci (204), a liczy sie tylko status.
    const res = await fetch(`/api/recenzje/linki/${encodeURIComponent(id)}/uniewaznij`, {
      method: "POST",
      credentials: "same-origin",
    });
    if (!res.ok) throw await bladZOdpowiedzi(res);
  },

  /** Poprawki recenzentow, od najnowszych. Filtry to parametry zapytania (puste pomijamy). */
  recenzjePoprawki: async (filtr: FiltrPoprawek = {}): Promise<PoprawkaAdmin[]> =>
    listaZOdpowiedzi<PoprawkaAdmin>(
      await request<unknown>(`/api/recenzje/poprawki${zapytanie({ ...filtr })}`),
      "poprawki",
    ),

  /**
   * Te same poprawki jako plik CSV (GET /api/recenzje/poprawki.csv). Przez fetch, nie zwykly link: odmowa serwera
   * wraca jako zdanie w interfejsie, a nie jako strona z JSON-em.
   */
  recenzjePoprawkiCsv: async (filtr: FiltrPoprawek = {}): Promise<{ blob: Blob; nazwaPliku: string }> => {
    const res = await fetch(`/api/recenzje/poprawki.csv${zapytanie({ ...filtr })}`, { credentials: "same-origin" });
    if (!res.ok) throw await bladZOdpowiedzi(res);
    const blob = await res.blob();
    return { blob, nazwaPliku: nazwaCsvZNaglowka(res.headers.get("Content-Disposition")) };
  },
};
